import React, { FC, useState, useEffect, useContext } from 'react';

import AddIcon from '@material-ui/icons/AddCircle';
import { Hidden } from '@material-ui/core';

import { GlobalContext } from '../../../../../../store';
import { IAdminFlight, TFlightCategory } from '../../../../../../store/admin/eventFlights/store';

import Button from '../../../../../../components/Button';
import FlightRow from './Components/FlightRow';
import { getEmptyFlight } from './Utils/getEmptyFlight';
import { isFlightInvalid } from './Utils/isFlightValid';
import { sortBy } from '../../../../../../contrib/sortBy';
import { useRouteInfoState } from '../../../../../../components/RouteInfo/Context/RouteInfo.context';
import { FlightRowStyled } from './FlightRow.Styles';
import { FlightRowsContainerStyled } from './FlightRowsContainer.Styles';
import { useGetAdminEvent } from '../../../../../../contrib/Hooks/useGetAdminEvent';

interface IProps {
  category: TFlightCategory;
  onLengthChange: (flightListLength: number) => void;
  onNotSavedChange: (flightsNotSaved: boolean) => void;
}

const FlightsRows: FC<IProps> = props => {
  const { onLengthChange, onNotSavedChange, category } = props;

  const { eventId, clubId } = useRouteInfoState();

  const {
    actions: {
      adminEvents: { editFlight, createFlight, removeFlight },
      notifications: { pushError }
    }
  } = useContext(GlobalContext);

  const event = useGetAdminEvent(eventId);

  const [flights, setFlights] = useState<Array<Partial<IAdminFlight>>>([]);
  const [unsavedFlights, setUnsavedFlights] = useState<number[]>([]);
  const [newFlight, setNewFlight] = useState<IAdminFlight>();
  const [displayNewFlight, setDisplayNewFlight] = useState<boolean>(false);
  const [automaticHandicaps, setAutomaticHandicaps] = useState(false);
  const [flightsByAge, setFlightsByAge] = useState(false);
  const [flightsByHandicap, setFlightsByHandicap] = useState(false);

  const onFlightSetup = (field: string, index: number) => (evnt: any): void => {
    const value = evnt.target ? evnt.target.value : evnt.value;

    const notSaved = [...unsavedFlights];

    const isIn = notSaved.find((i: number) => i === index);

    if (!Number.isNaN(value)) {
      if (isIn === undefined) {
        notSaved.push(index);
      }

      const newFlights = [...flights];

      newFlights[index][field as keyof IAdminFlight] = value;

      setFlights(newFlights);
      setUnsavedFlights(notSaved);

      onNotSavedChange(!allFlightsAreValid());
    }
  };

  const newFlightSetup = (field: string) => (evnt: any): void => {
    if (newFlight) {
      const value = evnt.target ? evnt.target.value : evnt.value;

      setNewFlight({ ...newFlight, [field]: value });
      setUnsavedFlights([...unsavedFlights, -1]);

      onNotSavedChange(!allFlightsAreValid());
    }
  };

  const flightDelete = async (flight: IAdminFlight) => {
    const response = await removeFlight(Number(clubId), flight);

    if (response.errors) {
      pushError('The flight could not be modified');

      return false;
    }

    return true;
  };

  const deleteFlight = (index: number, flight: IAdminFlight) => async () => {
    const newFlights = [...flights];

    if (!newFlights) {
      return;
    }

    if (flight.id) {
      if (newFlights.length > 1) {
        const response: any = await flightDelete(flight);

        if (!!response) {
          const finalFlights: Array<Partial<IAdminFlight>> = newFlights.filter(
            f => f.id !== flight.id
          );
          setFlights(finalFlights);
        }
      }
    }

    onLengthChange(newFlights.length);

    const notSaved: number[] = unsavedFlights.filter((i: number) => i !== index);
    setUnsavedFlights(notSaved);
    onNotSavedChange(notSaved.length > 0);
  };

  const addFlight = (): void => {
    const notSaved = [...unsavedFlights, -1];

    setFlights(flights);
    setUnsavedFlights(notSaved);

    if (eventId) {
      setNewFlight(getEmptyFlight(eventId, category));
    }

    setDisplayNewFlight(true);

    onLengthChange((flights || []).length + 1);
    onNotSavedChange(notSaved.length > 0);
  };

  const allFlightsAreValid = (): boolean => {
    if (!flights) {
      return false;
    }

    const flightsToValidate =
      displayNewFlight && newFlight ? [...flights, newFlight] : [...flights];

    return flightsToValidate.reduce(
      (allFlightsValid: boolean, flight: Partial<IAdminFlight>) =>
        allFlightsValid &&
        !isFlightInvalid(flight, flightsByAge, flightsByHandicap, automaticHandicaps),
      true
    );
  };

  const flightSetup = async (flight: IAdminFlight) => {
    const { ageLower, ageUpper, gender, handicapLower, handicapUpper } = flight;

    const setupFlight = {
      ...flight,
      ageLower: flightsByAge && ageLower ? ageLower : undefined,
      ageUpper: flightsByAge && ageUpper ? ageUpper : undefined,
      gender: gender && gender !== 'B' ? gender : null,
      handicapLower: flightsByHandicap && handicapLower ? handicapLower : undefined,
      handicapUpper: flightsByHandicap && handicapUpper ? handicapUpper : undefined
    };

    const response = setupFlight.id
      ? await editFlight(Number(clubId), setupFlight)
      : await createFlight(Number(clubId), setupFlight);

    if (response.errors) {
      pushError('The flight could not be modified');

      return false;
    }

    return true;
  };

  const saveFlight = (index: number, flight: IAdminFlight) => async (): Promise<any> => {
    const isANewFlight = flight.id < 0;
    if (isANewFlight) {
      delete flight.id;
    }

    const response = await flightSetup(flight);

    if (!!response) {
      const notSaved: number[] = unsavedFlights.filter((i: number) => i !== index && index !== -1);

      if (isANewFlight) {
        setDisplayNewFlight(false);

        setNewFlight(undefined);
      }

      setUnsavedFlights(notSaved);

      onNotSavedChange(notSaved.length > 0);
    }
  };

  const removeNewFlight = () => () => {
    setDisplayNewFlight(flights && flights.length <= 0);

    if (eventId) {
      setNewFlight(getEmptyFlight(eventId, category));
    }

    setUnsavedFlights(unsavedFlights.filter((index: number) => index !== -1));
  };

  useEffect(() => {
    if (event) {
      const filteredFlights = event.flights.filter(filter => filter.category === category);

      setFlights(sortBy(filteredFlights, 'asc', 'id'));

      setAutomaticHandicaps(event.automaticHandicaps);

      setFlightsByAge(event.flightsByAge);
      setFlightsByHandicap(event.flightsByHandicap);
    }
  }, [event, category]);

  useEffect(() => {
    onNotSavedChange(!allFlightsAreValid());
  }, [flightsByAge]);

  return (
    <FlightRowsContainerStyled className="admin-form-radios column flights">
      {flights.length > 0 && (
        <Hidden smDown={true}>
          <FlightRowStyled className="table-title">
            <div className="table-title-name">Name the Flight</div>

            <div className="table-title-sex">Sex</div>

            {flightsByAge && <div className="table-title-range">Age range</div>}

            {flightsByHandicap && <div className="table-title-range">Index range</div>}
          </FlightRowStyled>
        </Hidden>
      )}

      {flights.map((flight: any, index: number) => (
        <FlightRow
          automaticHandicaps={automaticHandicaps}
          deleteFlight={deleteFlight}
          flight={flight}
          flightsByAge={flightsByAge}
          flightsByHandicap={flightsByHandicap}
          flightSetup={onFlightSetup}
          flightsLength={(flights || []).length}
          index={index}
          key={`flight-item-${index}`}
          saveFlight={saveFlight}
          unsavedFlights={unsavedFlights}
        />
      ))}

      {displayNewFlight && newFlight && (
        <FlightRow
          automaticHandicaps={automaticHandicaps}
          deleteFlight={removeNewFlight}
          flight={newFlight}
          flightsByAge={flightsByAge}
          flightsByHandicap={flightsByHandicap}
          flightSetup={newFlightSetup}
          index={-1}
          saveFlight={saveFlight}
          unsavedFlights={unsavedFlights}
        />
      )}

      <Button
        color="primary"
        onClick={addFlight}
        className="add-button"
        disabled={unsavedFlights.length > 0}
      >
        <AddIcon color="primary" />

        <span className="add-button-text">ADD ANOTHER ITEM</span>
      </Button>
    </FlightRowsContainerStyled>
  );
};

export default FlightsRows;
